import React, {useCallback, useEffect, useState} from 'react';
import {get, post} from '../../core/http';
import {API} from '../../core/constants';
import {formatTimestamp} from '../../utils/format-date';
import AssignArticleDialog from '../AssignArticleDialog';
import EditUnsortedDialog from '../EditUnsortedDialog';
import ImportArticleDialog from './ImportArticleDialog';
import debounce from 'lodash.debounce';
import Badge from '../ui/Badge';

const SOURCE_LABELS = {
    'reddit-article': 'Reddit → article',
    'reddit-image': 'Reddit image (OCR)',
    'reddit-selftext': 'Reddit post',
    'manual-import': 'Manual import',
};

const ArticlesUnsorted = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedArticles, setSelectedArticles] = useState([]);
    const [assignArticle, setAssignArticle] = useState(null);
    const [editArticle, setEditArticle] = useState(null);
    const [importing, setImporting] = useState(false);

    const [filteredArticles, setFilteredArticles] = useState([]);

    const getArticles = useCallback(async () => {
        try {
            const result = await get(`${API}/unsorted`);

            if (result.body) {
                const sorted = [...result.body].sort((a, b) => (b.created || 0) - (a.created || 0));
                setArticles(sorted);
                setFilteredArticles(sorted);
            }
        } catch (error) {
            console.log('Could not retrieve articles');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleArticleCheck = (event, article) => {
        if (event.target.checked) {
            setSelectedArticles((prev) => [...prev, article.id]);
        } else {
            setSelectedArticles((prev) => prev.filter((a) => a !== article.id));
        }
    };

    const removeFromLists = (id) => {
        setArticles((curr) => curr.filter((a) => a.id !== id));
        setFilteredArticles((curr) => curr.filter((a) => a.id !== id));
    };

    const handleAssigned = (success) => {
        if (success && assignArticle) {
            removeFromLists(assignArticle.id);
        }

        setAssignArticle(null);
    };

    const handleArchive = async (article) => {
        try {
            await post(`${API}/unsorted/${article.id}/archive`);
            removeFromLists(article.id);
        } catch (error) {
            console.log('Could not archive article', error);
        }
    };

    const handleSaved = (updated) => {
        setArticles((curr) => curr.map((a) => (a.id === updated.id ? updated : a)));
        setFilteredArticles((curr) => curr.map((a) => (a.id === updated.id ? updated : a)));
        setEditArticle(null);
    };

    const handleImported = async () => {
        setImporting(false);
        await getArticles();
    };

    const handleSearch = (event) => {
        if (event.target.value === '') {
            setFilteredArticles(articles);
        } else {
            const query = event.target.value.toLowerCase();
            setFilteredArticles(
                articles.filter(
                    (article) =>
                        article.title?.toLowerCase().includes(query) ||
                        article.summary?.toLowerCase().includes(query) ||
                        article.domain?.toLowerCase().includes(query) ||
                        article.subreddit?.toLowerCase().includes(query)
                )
            );
        }
    };

    const debouncedChangeHandler = useCallback(
        debounce(handleSearch, 300)
        , [articles]
    );

    useEffect(() => {
        getArticles();
    }, [getArticles]);

    if (!articles?.length && loading) {
        return <p className="text-center my-19">Retrieving articles...</p>;
    }

    return (
        <div>
            <div className="flex flex-wrap justify-between items-end gap-3 mb-4">
                <div className="w-full sm:w-2/3">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold">Unsorted articles</h1>
                        <span className="badge badge-ghost badge-sm font-normal">{articles.length}</span>
                    </div>
                    <p className="text-sm text-base-content/60 mt-0.5">Articles that need to be assigned to an event (or edited/archived)</p>

                    <input placeholder={"Search title, summary, domain, subreddit..."} className={"input input-bordered input-sm w-full mt-2"} type="text"
                           onChange={debouncedChangeHandler}/>
                </div>
                <div className="flex gap-1.5">
                    <button className="btn btn-outline btn-sm" onClick={() => setImporting(true)}>
                        Import from URL
                    </button>
                    {selectedArticles.length ? (
                        <>
                            <button className="btn btn-primary btn-sm">Bulk assign</button>
                            <button className="btn btn-error btn-sm">Bulk archive</button>
                        </>
                    ) : null}
                </div>
            </div>

            {!loading && !articles.length ? (
                <p className="text-center text-sm text-base-content/60 py-10">No unsorted articles</p>
            ) : (
                <div className="bg-base-100 rounded-lg border border-base-300 overflow-x-auto">
                <table className="table table-sm">
                    <thead>
                    <tr>
                        <th></th>
                        <th>Title / Summary</th>
                        <th>Tags</th>
                        <th>Source</th>
                        <th>Created</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredArticles.map((article) => (
                        <tr key={article.id}>
                            <th>
                                <label>
                                    <input type="checkbox" className="checkbox"
                                           onChange={(event) => handleArticleCheck(event, article)}/>
                                </label>
                            </th>
                            <td className="max-w-[400px]">
                                <div className="font-bold">{article.title}</div>
                                {article.summary && (
                                    <div className="text-sm opacity-80 mt-1">
                                        {article.summary.length > 180 ? `${article.summary.slice(0, 180)}...` : article.summary}
                                    </div>
                                )}
                                {article.aiMatchedEventId && (
                                    <div className="mt-1">
                                        <Badge tone="accent">AI match: {article.aiMatchReason || 'possible existing event'}</Badge>
                                    </div>
                                )}
                            </td>
                            <td>{article.tags}</td>
                            <td>
                                <div>{article.domain}</div>
                                {article.sourceType && (
                                    <div className="text-xs opacity-70">
                                        {SOURCE_LABELS[article.sourceType] || article.sourceType}
                                        {article.subreddit ? ` (r/${article.subreddit})` : ''}
                                    </div>
                                )}
                            </td>
                            <td>{formatTimestamp(article.created)}</td>
                            <th>
                                <div className="text-center space-x-1 flex flex-wrap gap-1">
                                    <button className="btn btn-secondary btn-xs" onClick={() => setEditArticle(article)}>
                                        Edit
                                    </button>
                                    <button className="btn btn-primary btn-xs" onClick={() => setAssignArticle(article)}>
                                        Assign
                                    </button>
                                    <a href={article.url} target="_blank" rel="noreferrer"
                                       className="btn btn-accent btn-xs">
                                        Read
                                    </a>
                                    <button className="btn btn-error btn-xs" onClick={() => handleArchive(article)}>
                                        Archive
                                    </button>
                                </div>
                            </th>
                        </tr>
                    ))}
                    </tbody>
                    <tfoot>
                    <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="text-right">Total unsorted: {articles.length}</th>
                    </tr>
                    </tfoot>
                </table>
                </div>
            )}

            {assignArticle && <AssignArticleDialog article={assignArticle} onAssigned={handleAssigned}/>}
            {editArticle && <EditUnsortedDialog article={editArticle} onSaved={handleSaved} onCancel={() => setEditArticle(null)}/>}
            {importing && <ImportArticleDialog onImported={handleImported} onCancel={() => setImporting(false)}/>}
        </div>
    );
};

export default ArticlesUnsorted;
